import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const KEY = 'vox_cookie_consent'; // 'accepted' | 'rejected'
export const COOKIE_PREFS_EVENT = 'vox:open-cookie-preferences';

/** Re-open the consent banner from anywhere (e.g. a footer link). */
export const openCookiePreferences = () => {
  window.dispatchEvent(new Event(COOKIE_PREFS_EVENT));
};

/** Update Google Consent Mode v2 (ID-independent; no-op if gtag absent). */
const applyConsent = (granted) => {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: granted ? 'granted' : 'denied',
        ad_storage: granted ? 'granted' : 'denied',
      });
    }
  } catch (e) { /* no-op */ }
};

/**
 * Bottom cookie-consent banner. Shows once until the visitor chooses. Stores
 * the choice in localStorage and syncs Google Consent Mode. Links to the
 * (admin-managed) Cookie Policy page.
 */
const CookieConsentBanner = () => {
  const navigate = useNavigate();
  const [choice, setChoice] = useState(() => {
    try { return localStorage.getItem(KEY); } catch { return 'accepted'; }
  });

  // Re-apply stored consent to gtag on load
  useEffect(() => {
    if (choice) applyConsent(choice === 'accepted');
  }, [choice]);

  // Allow re-opening the banner from a "Cookie preferences" link anywhere
  useEffect(() => {
    const reopen = () => setChoice(null);
    window.addEventListener(COOKIE_PREFS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_PREFS_EVENT, reopen);
  }, []);

  if (choice) return null;

  const decide = (accepted) => {
    const val = accepted ? 'accepted' : 'rejected';
    try { localStorage.setItem(KEY, val); } catch { /* ignore */ }
    applyConsent(accepted);
    setChoice(val);
  };

  return (
    <div
      data-testid="cookie-consent-banner"
      className="vs-cookie-banner"
      style={{
        position: 'fixed', left: '16px', right: '16px',
        zIndex: 9500, maxWidth: '720px', margin: '0 auto',
        background: 'rgba(44,58,76,0.97)', backdropFilter: 'blur(12px)',
        border: '1px solid rgba(215,179,119,0.4)', borderRadius: '14px',
        padding: '18px 20px', boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px',
        fontFamily: 'DM Sans, sans-serif',
      }}
    >
      <p style={{ flex: '1 1 300px', margin: 0, color: '#F7F3E9', fontSize: '14px', lineHeight: 1.55 }}>
        We use cookies to keep you signed in and to understand how VoxSmyth is used.{' '}
        <button
          data-testid="cookie-policy-link"
          onClick={() => navigate('/cookie-policy')}
          style={{ background: 'none', border: 'none', color: '#D7B377', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px', padding: 0 }}
        >
          Read our Cookie Policy
        </button>.
      </p>
      <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
        <button
          data-testid="cookie-reject-btn"
          onClick={() => decide(false)}
          style={{ padding: '10px 18px', borderRadius: '10px', border: '1px solid rgba(215,179,119,0.4)', background: 'transparent', color: '#CBD5E0', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
        >
          Reject
        </button>
        <button
          data-testid="cookie-accept-btn"
          onClick={() => decide(true)}
          style={{ padding: '10px 22px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#D7B377,#E2D7C4)', color: '#1A202C', fontSize: '14px', fontWeight: 800, cursor: 'pointer' }}
        >
          Accept
        </button>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
